import { Point3D } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { DesignClientCamera } from '../../../three/DesignClientCamera';
import { getLayoutModeCameraControlsOrNull } from '../../../three/reactThreeFiberBindings';

export type RenderMode = 'layout' | 'design';

export const DESIGN_CAMERAS = ['orthographic-design'] as const;
export const LAYOUT_CAMERAS = ['orthographic-layout', 'perspective-layout'] as const;

export type DesignCameraType = (typeof DESIGN_CAMERAS)[number];
export type LayoutCameraType = (typeof LAYOUT_CAMERAS)[number];

export type CameraType = DesignCameraType | LayoutCameraType;

export function getRenderModeForCameraType(type: CameraType): RenderMode {
  switch (type) {
    case 'orthographic-layout':
    case 'perspective-layout':
      return 'layout';

    case 'orthographic-design':
      return 'design';

    default:
      assertExhaustiveSwitch(type);
  }
}

export interface Rotation {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
}

export interface CameraProperties {
  readonly zoom: number;
  readonly position: Point3D;
  readonly rotation: Rotation;
  readonly target: Point3D | null;
}

export interface DesignCameraSettings {
  readonly flipped: boolean;
  readonly reversed: boolean;
}

// ensures a rotation is a plain object so redux state stays clean
export function sanitizeRotation(rotation: Rotation) {
  return {
    x: rotation.x,
    y: rotation.y,
    z: rotation.z,
    w: rotation.w,
  };
}

// ensures a position is a plain object so redux state stays clean
export function sanitizePosition(position: Point3D) {
  return {
    x: position.x,
    y: position.y,
    z: position.z,
  };
}

export function serializeCamera(camera: DesignClientCamera): CameraProperties {
  const controls = getLayoutModeCameraControlsOrNull();

  return {
    zoom: camera.zoom,
    position: sanitizePosition(camera.position),
    rotation: sanitizeRotation(camera.quaternion),
    target: controls ? sanitizePosition(controls.target) : null,
  };
}
